import { useCallback, useEffect, useState, useRef, ChangeEvent } from "react";
import style from "./multiRangeSlider.module.scss";
import { useTranslations } from "next-intl";
import { FilterStatsRangeDto } from "@/projectRoot/api-client/interfaces/response/products/FilterStats.dto";
import { useFilters } from "@/projectRoot/src/contexts/FiltersContext";
import Minus from "@/projectRoot/src/components/common/svg/Minus";
import Plus from "@/projectRoot/src/components/common/svg/Plus";

export interface PriceRange {
  start: FilterStatsRangeDto,
  current?: FilterStatsRangeDto
}
interface Props {
  opened: boolean;
  openGroup: () => void;
  classes?: string[];
}

const MultiRangeSlider = ({ opened, openGroup, classes = [] }: Props) => {
  const {filters, dispatch} = useFilters();
  const t = useTranslations();
  const range = useRef<HTMLDivElement | null>(null);
  const [priceRange, setPriceRange] = useState<PriceRange>(
    {
      start: { 
        min: (Math.floor((filters.meta?.filters?.prices?.min ?? 1) / 1000) * 1000), 
        max: (Math.ceil((filters.meta?.filters?.prices?.max ?? 1) / 1000) * 1000) 
      },
    }
  );
  const [minVal, setMinVal] = useState<number>(priceRange.current ? priceRange.current.min : priceRange.start.min);
  const [maxVal, setMaxVal] = useState<number>(priceRange.current ? priceRange.current.max : priceRange.start.max);
  const minValRef = useRef<number>(priceRange.current ? priceRange.current.min : priceRange.start.min);
  const maxValRef = useRef<number>(priceRange.current ? priceRange.current.max : priceRange.start.max);
  const step: number = 1000;

  // Convert to percentage
  const getPercent = useCallback((value: any) => {
    return Math.round(((value - priceRange.start.min) / (priceRange.start.max - priceRange.start.min)) * 100)
  }, [priceRange.start]);

  const setMinValue = (e: ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(Number(e.target.value), maxVal - step);
    // setMinVal(value < maxVal * 0.9 ? value : Math.floor(maxVal * 0.9));
    setMinVal(value);
    minValRef.current = value;
  }

  const setMaxValue = (e: ChangeEvent<HTMLInputElement>) => {
    const value = Math.max(Number(e.target.value), minVal + step);
    // setMaxVal(value > minVal * 1.1 ? value : Math.floor(minVal * 1.2));
    setMaxVal(value);
    maxValRef.current = value;
  }

  // Set width of the range to decrease from the left side
  useEffect(() => {
    const minPercent = getPercent(minVal);
    const maxPercent = getPercent(maxValRef.current);
    if (range.current) {
      range.current.style.left = `${minPercent}%`;
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [minVal, getPercent]);

  // Set width of the range to decrease from the right side
  useEffect(() => {
    const minPercent = getPercent(minValRef.current);
    const maxPercent = getPercent(maxVal);

    if (range.current) {
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [maxVal, getPercent]);

  useEffect(() => {
    let priceRange: PriceRange = {
      start: { min: (Math.floor((filters.meta?.filters?.prices?.min ?? 1) / 1000) * 1000), max: (Math.ceil((filters.meta?.filters?.prices?.max ?? 1) / 1000) * 1000) },
    };
    if (filters.state?.price_min || filters.state?.price_max) {
      priceRange.current = { min: filters.state?.price_min ?? (Math.floor((filters.meta?.filters?.prices?.min ?? 1) / 1000) * 1000), max: filters.state?.price_max ?? (Math.ceil((filters.meta?.filters?.prices?.max ?? 1) / 1000) * 1000) }
    }
    setPriceRange(priceRange);
  }, [filters])

  useEffect(() => {
    const minValue: number = Math.min(priceRange.start.min, priceRange.start.max - step);
    const maxValue: number = Math.max(priceRange.start.max, priceRange.start.min + step);
    if (!priceRange.current) {
      setMinVal(minValue);
      minValRef.current = minValue;
      setMaxVal(maxValue);
      maxValRef.current = maxValue;
    } else {
      if (priceRange.current.min < minValue) {
        setMinVal(minValue);
        minValRef.current = minValue;
      } else {
        if (filters.state?.price_min) {
          setMinVal(Math.min(priceRange.current.min, priceRange.start.max - step));
          minValRef.current = Math.min(priceRange.current.min, priceRange.start.max - step);
        }
      }
      if (priceRange.current.max > maxValue) {
        setMaxVal(maxValue);
        maxValRef.current = maxValue;
      } else {
        if (filters.state?.price_max) {
          setMaxVal(Math.max(priceRange.current.max, priceRange.start.min + step));
          maxValRef.current = Math.max(priceRange.current.max, priceRange.start.min + step);
        }
      }
    }
  }, [priceRange])

  return (
    <div className={`${style.filterRangeWrapper} ${opened ? style.opened : ''} ${classes.join(' ')}`}>
      <div className={style.rangeSliderHeader} onClick={() => openGroup()}>
        <div className={style.title}>{t('price')}</div>
        <span className={style.dropdown}>
            {
                opened ? <Minus /> : <Plus />
            }
        </span>
      </div>
      <div className={style.rangeSliderBody}>
        <div className={style.priceRangeWrapper}>
          <div className={style.priceMin}>
            <span>{minVal}</span>
          </div>
          <div className={style.priceMax}>
            <span>{maxVal}</span>
          </div>
        </div>
        <div className={style.rangeSlider}>
          <div className={style.progress} style={{ left: '0', right: '0' }} ref={range}></div>
        </div>
        <div className={style.rangeInputs}>
          <input
            type="range"
            step={step}
            className={style.rangeMin}
            style={{ zIndex: minVal + 1000 == priceRange.start.max ? 2 : 1 }}
            onChange={(event) => setMinValue(event)}
            onMouseUp={() => dispatch({ type: "updatePriceRange", payload: { min: minVal, max: maxVal } })}
            onTouchEnd={() => dispatch({ type: "updatePriceRange", payload: { min: minVal, max: maxVal } })}
            onKeyUp={(event) => { event.code == 'ArrowLeft' || event.code == 'ArrowRight' ? dispatch({ type: "updatePriceRange", payload: { min: minVal, max: maxVal } }) : void (null) }}
            min={priceRange.start.min}
            max={priceRange.start.max}
            value={minVal}

          />
          <input
            type="range"
            step={step}
            className={style.rangeMax}
            onChange={(event) => setMaxValue(event)}
            onMouseUp={() => dispatch({ type: "updatePriceRange", payload: { min: minVal, max: maxVal } })}
            onTouchEnd={() => dispatch({ type: "updatePriceRange", payload: { min: minVal, max: maxVal } })}
            onKeyUp={(event) => { event.code == 'ArrowLeft' || event.code == 'ArrowRight' ? dispatch({ type: "updatePriceRange", payload: { min: minVal, max: maxVal } }) : void (null) }}
            min={priceRange.start.min}
            max={priceRange.start.max}
            value={maxVal}
          />
        </div>

      </div>
    </div>
  );
};

export default MultiRangeSlider;
